import { EPaths } from 'router/const/paths';

import { cookies } from 'utils/cookies';
import { _fetch } from 'utils/fetch';

import { API } from 'constants/api';
import { searchParamsNames } from 'constants/searchParamsNames';

import type { TOKEN_TYPE } from './constants/tokenNames';
import type { ErrorResponse, IRecord, VideoCall, VOXUser } from './declare';
import { deleteTokenFromSearchParams } from './helpers/deleteTokenFromSearchParams';
import { getTokenMap } from './helpers/getTokenMap';

export class Provider {
    /** Токен пользователя */
    private _token!: string;

    /** Название токена пользователя */
    private _tokenName!: TOKEN_TYPE;

    constructor() {
        this.updateToken();
    }

    /** Обновить информацию токена для экземпляра */
    public updateToken() {
        if (!process.env.IS_STORYBOOK_ENV) {
            const tokenMap = getTokenMap();

            if (tokenMap) {
                const { token, tokenName } = tokenMap;

                cookies.set(tokenName, token);
                deleteTokenFromSearchParams();
                this.storeToken(tokenMap);
            } else if (window.location.pathname !== '/auth') {
                window.location.assign(`/${EPaths.AUTH}?${searchParamsNames.redirectTo}=${window.location.pathname}${window.location.search}`);
            }
        }
    }

    /** Установка токена в экземпляр */
    private storeToken({ token, tokenName }: { token: string; tokenName: TOKEN_TYPE }) {
        this._tokenName = tokenName;
        this._token = token;
    }

    /** Получить строку с параметрами токена на основе типа пользователя */
    private getTokenParamString() {
        const parameters = new URLSearchParams();

        parameters.append(this.tokenName, this._token);

        return `${parameters}`;
    }

    /** Получить токен для VOX */
    public getVOXToken = (oneTimeKey: string) => {
        return _fetch<{ token: string }>(`${API.VIDEO_CALL_API}/token?${this.getTokenParamString()}&key=${oneTimeKey}`);
    };

    /** Добавление пользователя в VOX */
    public addUser = () => {
        return _fetch<VOXUser | ErrorResponse>(
            `${API.VIDEO_CALL_API}/users?${this.getTokenParamString()}`,
            {
                method: 'post',
            },
            { unwrapDataProperty: false },
        );
    };

    /** Добавление стороннего пользователя в VOX */
    public addExternalUser = (userName: string) => {
        return _fetch(
            `${API.VIDEO_CALL_API}/b2c-external-users?${this.getTokenParamString()}`,
            {
                body: JSON.stringify({ name: userName }),
                method: 'post',
            },
            { unwrapDataProperty: false },
        );
    };

    /** Получить информацию о конференции */
    public getVideoCallInfo = (conferenceId: string) => {
        return _fetch<VideoCall | ErrorResponse>(`${API.VIDEO_CALL_API}/${conferenceId}?${this.getTokenParamString()}`);
    };

    /** Присоединиться к конференции. Используется для записи метрик */
    public joinVideoCall = (conferenceId: string) => {
        return _fetch<VideoCall>(`${API.VIDEO_CALL_API}/${conferenceId}/join?${this.getTokenParamString()}`);
    };

    /** Получить информацию о пользователе */
    public static getUserData = (userId: string) => {
        return _fetch<VOXUser>(`${API.VIDEO_CALL_API}/users/${userId}?apiKey=${API.API_KEY}`, undefined, { unwrapDataProperty: false });
    };

    /** Текущее название токена для экземпляра */
    public get tokenName() {
        return this._tokenName;
    }

    /** Текущее значение токена для экземпляра */
    public get token() {
        return this._token;
    }

    /** Проверка факта существования записи конференции */
    public static getConferenceRecord = (link: string) => {
        return _fetch<Response>(link, { method: 'HEAD' });
    };

    /** Покинуть конференцию. Используется для записи метрик */
    public leave = (conferenceId: string) => {
        return _fetch(`${API.VIDEO_CALL_API}/${conferenceId}/leave?${this.getTokenParamString()}`);
    };

    /** Получение информации о конференции для просмотра записи конференции. Используется для записи метрик */
    public getConferenceDataForRecord = (conferenceId: string) => {
        return _fetch<IRecord | ErrorResponse>(`${API.VIDEO_CALL_API}/${conferenceId}/record?${this.getTokenParamString()}`);
    };
}

/** объект для запроса данных */
export const provider = new Provider();
